import React from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { UseQueryResult } from "@tanstack/react-query";

import { CartResponse } from "@/networking/carts.api";
import {
  capitalizeFirst,
  pluralPointsLabel,
  pointsLabel,
} from "@/lib/points-label";
import PricingLoader from "../loaders/pricing-loader";
import { formatCurrency, formatDate, getRecurringItems } from "@/lib/utils";
import FluidImage from "../images/fluid-image";
import { FLUID_CDN_URL } from "@/lib/constants";
import { Badge } from "../fluid-ui/badge";
import {
  canRenderInclusiveBreakdown as canRenderInclusiveBreakdownHelper,
  getInclusiveTaxLabelParams,
} from "@/lib/inclusive-tax-label";

export default function PricingSection({
  query,
  discountLoading,
  onRemoveDiscount,
  deleteDiscountLoading,
}: {
  query: UseQueryResult<CartResponse | null>;
  discountLoading: boolean;
  onRemoveDiscount: (discountCode: string) => void;
  deleteDiscountLoading: boolean;
}) {
  const { t, language } = useTranslation();
  const { cart } = query.data || {};

  // Calculate recurring items data using utility functions
  const recurringItems = getRecurringItems(cart);
  const currencyCode = cart?.currency_code;

  const shippingTotalNum = cart?.shipping_total ?? 0;
  const hasFreeShippingPromo =
    (cart?.shipping_discount !== 0 &&
      cart?.shipping_discount === shippingTotalNum) ||
    (cart?.shipping_discount_codes?.length ?? 0) > 0;

  const showDiscountRow =
    cart?.discount_breakdown?.total_discounts &&
    cart?.discount_breakdown?.total_discounts > 0 &&
    cart?.discount_total &&
    (cart?.discount_total ?? 0) > 0;

  // Inclusive-tax display (CURRENT-1672). When the cart is priced inclusive
  // of tax, render an informational row "Tax {rate}% {name} (included in
  // pricing) {amount}" between Shipping and Total, and show the gross
  // catalog total as Subtotal (backend `sub_total` is the NET when inclusive).
  const priceInclusiveOfTax =
    cart?.price_inclusive_of_tax ?? cart?.country?.price_inclusive_of_tax;
  const priceInclusiveTaxName =
    cart?.price_inclusive_tax_name ?? cart?.country?.price_inclusive_tax_name;
  const inclusiveTaxLabel = getInclusiveTaxLabelParams({
    priceInclusiveOfTax,
    priceInclusiveTaxName,
    taxStandardRate: cart?.country?.tax_standard_rate,
  });
  // Gross subtotal = catalog price * qty = sub_total + tax_total.
  // Backend `sub_total` is the NET when inclusive; the UI displays the
  // catalog-equivalent gross figure so customers see the price they're
  // used to in shop listings. Gate both the gross-subtotal display and
  // the inclusive tax row on the same condition: we must be able to
  // compute gross. If either numeric field is missing, fall through to
  // the legacy exclusive Tax row + NET subtotal so the math stays
  // consistent (NET + Tax + Shipping = Total) rather than rendering NET
  // beside an "included in pricing" tax row that contradicts it.
  const canRenderInclusive = canRenderInclusiveBreakdownHelper({
    priceInclusiveOfTax,
    priceInclusiveTaxName,
    netSubtotal: cart?.sub_total,
    tax: cart?.tax_total,
  });
  const inclusiveGrossSubtotal =
    canRenderInclusive && cart
      ? formatCurrency(cart.sub_total + cart.tax_total, {
          symbol: cart.currency_symbol,
          currencyCode: cart.currency_code,
        })
      : null;
  const subtotalInCurrency =
    inclusiveGrossSubtotal ?? cart?.sub_total_in_currency;
  const showInclusiveTaxRow =
    inclusiveTaxLabel !== null &&
    canRenderInclusive &&
    (cart?.tax_total ?? 0) > 0;

  return (
    <div className="pb-5">
      {query.isLoading || deleteDiscountLoading || discountLoading ? (
        <PricingLoader />
      ) : (
        <>
          <div className="flex justify-between">
            <dt className="text-sm font-medium text-gray-500">
              {t("subtotal")}
            </dt>
            <dd className="text-sm font-medium text-gray-500">
              {subtotalInCurrency}
            </dd>
          </div>
          {cart?.points_applied &&
          cart.points_applied > 0 &&
          cart?.points_applied_amount_in_currency &&
          cart?.company?.reward_points_apply_to_subtotal ? (
            <div className="flex justify-between pt-2">
              <dt className="text-sm font-medium text-gray-500">
                {t("points_applied", undefined, {
                  label: capitalizeFirst(pluralPointsLabel(cart?.company)),
                })}{" "}
                (-{cart.points_applied.toLocaleString()}{" "}
                {pointsLabel(cart.points_applied, cart?.company)})
              </dt>
              <dd className="text-sm font-medium text-gray-500">
                -{cart.points_applied_amount_in_currency}
              </dd>
            </div>
          ) : null}
          {!cart?.digital_only && (
            <div className="flex justify-between pt-2">
              <div className="flex flex-wrap items-center gap-2">
                <dt className="text-sm font-medium text-gray-500">
                  {t("shipping")}
                </dt>
                {hasFreeShippingPromo &&
                  cart?.shipping_discount_codes?.map((discountCode) => (
                    <Badge
                      key={discountCode}
                      color="gray"
                      className="rounded px-2 text-gray-900"
                      size="sm"
                    >
                      <FluidImage
                        src={`${FLUID_CDN_URL}/images/checkout/icons/tag.svg`}
                        alt="Discount"
                        width={12}
                        height={12}
                      />
                      <span className="text-xs text-gray-600">
                        {discountCode}
                      </span>
                      <FluidImage
                        src={`${FLUID_CDN_URL}/images/checkout/icons/x.svg`}
                        alt="Remove discount"
                        width={6}
                        height={6}
                        className="cursor-pointer"
                        onClick={() => {
                          onRemoveDiscount(discountCode);
                        }}
                      />
                    </Badge>
                  ))}
              </div>
              <dd className="text-sm font-medium text-gray-500">
                {hasFreeShippingPromo
                  ? t("free")
                  : cart?.shipping_total_in_currency}
              </dd>
            </div>
          )}
          {!canRenderInclusive && (
            <div className="flex justify-between pt-2">
              <dt className="text-sm font-medium text-gray-500">
                {t("taxes")}
              </dt>
              <dd className="text-sm font-medium text-gray-500">
                {cart?.tax_total_in_currency}
              </dd>
            </div>
          )}
          {showInclusiveTaxRow && inclusiveTaxLabel !== null && (
            <div className="flex justify-between pt-2">
              <dt className="text-sm font-medium text-gray-500">
                {inclusiveTaxLabel.rate !== null
                  ? t("tax_included_in_pricing_with_rate", undefined, {
                      rate: inclusiveTaxLabel.rate,
                      name: inclusiveTaxLabel.name,
                    })
                  : t("tax_included_in_pricing", undefined, {
                      name: inclusiveTaxLabel.name,
                    })}
              </dt>
              <dd className="text-sm font-medium text-gray-500">
                {cart?.tax_total_in_currency}
              </dd>
            </div>
          )}
          {cart?.enrollment_fee_in_currency && cart?.enrollment_fee !== 0.0 ? (
            <div className="flex justify-between pt-2">
              <dt className="text-sm font-medium text-gray-500">
                {t("enrollment_fee")}
              </dt>
              <dd className="text-sm font-medium text-gray-500">
                {cart?.enrollment_fee_in_currency}
              </dd>
            </div>
          ) : null}
          {showDiscountRow ? (
            <div className="flex justify-between pt-2">
              <div className="overflow-wrap flex flex-wrap items-center gap-2">
                <dt className="text-sm font-medium text-gray-500">
                  {t("discount")}
                </dt>
                {cart?.product_discount_codes?.map((discountCode) => (
                  <Badge
                    key={discountCode}
                    color="gray"
                    className="rounded px-2 text-gray-900"
                    size="sm"
                  >
                    <FluidImage
                      src={`${FLUID_CDN_URL}/images/checkout/icons/tag.svg`}
                      alt="Discount"
                      width={12}
                      height={12}
                    />
                    <span className="text-xs text-gray-600">
                      {discountCode}
                    </span>
                    <FluidImage
                      src={`${FLUID_CDN_URL}/images/checkout/icons/x.svg`}
                      alt="Remove discount"
                      width={6}
                      height={6}
                      className="cursor-pointer"
                      onClick={() => {
                        onRemoveDiscount(discountCode);
                      }}
                    />
                  </Badge>
                ))}
              </div>
              <dd className="text-sm font-medium text-gray-500">
                -{cart?.discount_total_in_currency}
              </dd>
            </div>
          ) : null}
          {cart?.points_applied &&
          cart.points_applied > 0 &&
          cart?.points_applied_amount_in_currency &&
          !cart?.company?.reward_points_apply_to_subtotal ? (
            <div className="flex justify-between pt-2">
              <dt className="text-sm font-medium text-gray-500">
                {t("points_applied", undefined, {
                  label: capitalizeFirst(pluralPointsLabel(cart?.company)),
                })}{" "}
                (-{cart.points_applied.toLocaleString()}{" "}
                {pointsLabel(cart.points_applied, cart?.company)})
              </dt>
              <dd className="text-sm font-medium text-gray-500">
                -{cart.points_applied_amount_in_currency}
              </dd>
            </div>
          ) : null}
          <div className="flex items-center justify-between pt-4 text-gray-900">
            <dt className="text-sm font-medium text-gray-500">{t("total")}</dt>
            <dd className="text-base font-bold">
              <span className="text-xs font-medium text-gray-500">
                {currencyCode}
              </span>{" "}
              {(cart?.points_enabled || cart?.company?.reward_points_enabled) &&
              cart?.remaining_cart_amount_after_points_in_currency
                ? cart?.remaining_cart_amount_after_points_in_currency
                : cart?.amount_total_in_currency}
            </dd>
          </div>
          {cart?.buyer_rep_id && (cart?.qv_total || cart?.cv_total) ? (
            <>
              <div className="my-4 h-px w-full bg-gray-200" />
              <h3 className="text-sm font-medium text-gray-500">
                {t("volume")}
              </h3>
              {cart?.cv_total ? (
                <div className="flex justify-between pt-2">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("cv")}
                  </dt>
                  <dd className="text-sm font-medium text-gray-500">
                    {cart?.cv_total}
                  </dd>
                </div>
              ) : null}
              {cart?.qv_total ? (
                <div className="flex justify-between pt-2">
                  <dt className="text-sm font-medium text-gray-500">
                    {t("qv")}
                  </dt>
                  <dd className="text-sm font-medium text-gray-500">
                    {cart?.qv_total}
                  </dd>
                </div>
              ) : null}
            </>
          ) : null}
          {recurringItems.length > 0 && (
            <div className="mt-8 flex justify-between border-t border-b border-gray-200 py-4 text-sm">
              <dt className="flex items-center gap-1 text-gray-500">
                {t("recurring_subtotal")}
                <div className="relative cursor-default">
                  <div className="peer flex h-4 w-4 items-center justify-center rounded-full border border-gray-900 text-xs text-gray-900">
                    ?
                  </div>
                  <div className="invisible absolute bottom-full left-1/2 mb-2 w-56 -translate-x-1/2 rounded bg-gray-800 p-2 text-center text-xs text-white peer-hover:visible">
                    {t("not_applicable_discounts_message")}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -translate-y-1 border-4 border-transparent border-t-gray-800"></div>
                  </div>
                </div>
              </dt>
              <div className="flex flex-col gap-2">
                {cart?.recurring.map((item) => (
                  <dd
                    className="text-gray-500"
                    key={`${item.start_date}-${item.subtotal}`}
                  >
                    {item.subtotal_for_display} <br />
                    {item.start_date &&
                      `${t("starting")} ${formatDate(item.start_date, false, language)}`}
                  </dd>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
